import { useAuth } from "@clerk/nextjs";
import classNames from "classnames";
import Tooltip from "components/Tooltip";
import UserButton from "components/TopBar/UserButton";
import { HELP_CENTER_URL } from "constants/config";
import path from "constants/path";
import { useIsStatsigLoading } from "features/Statsig";
import NewThreadButton from "features/NewThreadButton";
import useLabels from "hooks/useLabels";
import { useAppDispatch } from "hooks/useStore";
import Link from "next/link";
import { setIsSearching } from "store/slices/search";
import { LogoMark } from "design_system/logo";

// SidebarButtonBase component with shared styles
type SidebarButtonBaseProps = {
  isSelected: boolean;
  iconClass: string;
  className?: string;
};

const SidebarButtonBase = ({
  isSelected,
  iconClass,
  className,
}: SidebarButtonBaseProps) => {
  return (
    <div className="flex justify-center items-center">
      <div
        className={classNames(
          "flex justify-center items-center w-9 h-9 rounded-lg hover:bg-bg-faint",
          isSelected ? "bg-bg-faint" : "",
          className
        )}
      >
        <i className={`text-xl text-fg-base ${iconClass}`} />
      </div>
    </div>
  );
};

// SidebarNavButton for navigation with Next.js Link
type SidebarNavButtonProps = {
  href: string;
  isSelected: boolean;
  iconClass: string;
};

const SidebarNavButton = ({
  href,
  isSelected,
  iconClass,
}: SidebarNavButtonProps) => {
  return (
    <Link
      href={href}
      onClick={(e) => {
        // Prevent default navigation if already on this page
        if (window.location.pathname == href) {
          e.preventDefault();
        }
      }}
    >
      <SidebarButtonBase isSelected={isSelected} iconClass={iconClass} />
    </Link>
  );
};

type SidebarCollapsedContentProps = {
  onExpand: () => void;
};

const SidebarCollapsedContent = ({
  onExpand,
}: SidebarCollapsedContentProps) => {
  const dispatch = useAppDispatch();
  const [sidebarLabels] = useLabels("screens.sidebar");

  const handleClickHome = () => {
    dispatch(setIsSearching(false));
  };

  const { isSignedIn, isLoaded } = useAuth();

  const isStatsigLoading = useIsStatsigLoading();

  return (
    <div className="flex flex-col items-stretch w-full h-full text-fg-base">
      <div className="flex sticky top-0 flex-col items-center">
        <Link
          href={path.HOME}
          onClick={(e) => {
            if (window.location.pathname === path.HOME) {
              e.preventDefault();
            }
            handleClickHome();
          }}
          className="flex justify-center items-center min-h-header"
        >
          <LogoMark height={24} />
        </Link>
        {isLoaded && !isStatsigLoading && (
          <div className="flex flex-col gap-1">
            <Tooltip content={sidebarLabels["new-thread"]} placement="right">
              <div className="mb-1">
                <NewThreadButton isCollapsed />
              </div>
            </Tooltip>
            <Tooltip content={sidebarLabels["home"]} placement="right">
              <div className="w-full">
                <SidebarNavButton
                  href={path.HOME}
                  isSelected={window.location.pathname == path.HOME}
                  iconClass="icon-home-converted"
                />
              </div>
            </Tooltip>
            {isSignedIn && (
              <>
                <Tooltip
                  content={sidebarLabels["history-title"]}
                  placement="right"
                >
                  <div className="w-full">
                    <SidebarNavButton
                      href={path.HISTORY}
                      isSelected={window.location.pathname.includes(
                        path.HISTORY
                      )}
                      iconClass="icon-history"
                    />
                  </div>
                </Tooltip>
                <Tooltip
                  content={sidebarLabels["research-hub"]}
                  placement="right"
                >
                  <div className="w-full">
                    <SidebarNavButton
                      href={path.RESEARCH_HUB}
                      isSelected={window.location.pathname.includes(
                        path.RESEARCH_HUB
                      )}
                      iconClass="icon-folder-converted"
                    />
                  </div>
                </Tooltip>
              </>
            )}
          </div>
        )}
      </div>
      <div className="flex-1 cursor-e-resize" onClick={onExpand}></div>
      <div className="flex flex-col gap-1">
        <Tooltip
          content={sidebarLabels["help-center"]}
          placement="right"
          offset={-2}
        >
          <div className="w-full">
            <SidebarNavButton
              href={HELP_CENTER_URL}
              isSelected={false}
              iconClass="icon-circle-help"
            />
          </div>
        </Tooltip>

        {isSignedIn && (
          <Tooltip
            content={sidebarLabels["account"]}
            placement="right"
            offset={-2}
          >
            <div className="flex justify-center items-center w-full">
              <UserButton placement="right-end" />
            </div>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

export default SidebarCollapsedContent;
